import React, { useContext, useEffect, useRef, useState } from "react";
import { useTheme } from "@mui/material";
import { ConfiguratorContext } from "../../../../../../contexts/ConfiguratorContext";
import DocumentDefinitionsColumn, {
  getOptionDisplayText,
  getOptionKey,
} from "../../../../shared/DocumentDefinitionsColumn";
import ActionSnackbar from "../../../../../../shared/components/ActionSnackbar";
import { FlexContainer } from "../../../../../../shared/components/styled/Container.styled";
import { DOCUMENTS_ALLOWED_TO_BE_DOWNLOADED_KEY } from "../../../../../../shared/constants/Configurator.constants";
import {
  DOCUMENT_VIEW_CATEGORY_LABELS,
  DOCUMENT_VIEW_CATEGORY_OTHER,
  DOCUMENT_VIEW_CATEGORY_STATEMENTS,
  DOCUMENT_VIEW_CATEGORY_TAX,
} from "../../../../../../shared/constants/StatementsDocuments.constants";
import { hex2rgba } from "../../../../../../shared/utils/utility-functions";
import {
  resolveInitialDocumentViewSelections,
  serializeDocumentViewSelections,
} from "../../../../../../shared/utils/documentViewSelections";
import { useIsMount } from "../../../../../../shared/hooks/useIsMount";

const VIEW_CATEGORIES = [
  DOCUMENT_VIEW_CATEGORY_STATEMENTS,
  DOCUMENT_VIEW_CATEGORY_TAX,
  DOCUMENT_VIEW_CATEGORY_OTHER,
];

const DocumentViewDefinitionsSetting = ({
  optionsByCategory = {},
  loading = false,
  configuredValue = "",
}) => {
  const theme = useTheme();
  const isFirstRender = useIsMount();
  const { addNewEdittedField } = useContext(ConfiguratorContext);
  const [selections, setSelections] = useState(() =>
    resolveInitialDocumentViewSelections(configuredValue, optionsByCategory),
  );
  const [duplicateInfoOpen, setDuplicateInfoOpen] = useState(false);
  const [duplicateInfoMessage, setDuplicateInfoMessage] = useState("");
  const hasInitializedSelections = useRef(false);

  const findOptionDisplayText = (key) => {
    for (const category of VIEW_CATEGORIES) {
      const option = (optionsByCategory[category] ?? []).find(
        (item) => getOptionKey(item) === key,
      );
      if (option) {
        return getOptionDisplayText(option);
      }
    }
    return key;
  };

  const formatCategoryList = (categories) => {
    const labels = categories.map(
      (category) => DOCUMENT_VIEW_CATEGORY_LABELS[category],
    );
    if (labels.length === 1) {
      return labels[0];
    }
    return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
  };

  useEffect(() => {
    if (hasInitializedSelections.current || loading) {
      return;
    }

    const hasOptions = VIEW_CATEGORIES.some(
      (category) => (optionsByCategory[category] ?? []).length > 0,
    );
    if (!hasOptions) {
      return;
    }

    setSelections(
      resolveInitialDocumentViewSelections(configuredValue, optionsByCategory),
    );
    hasInitializedSelections.current = true;
  }, [configuredValue, loading, optionsByCategory]);

  useEffect(() => {
    if (!hasInitializedSelections.current || isFirstRender) {
      return;
    }

    addNewEdittedField({
      key: DOCUMENTS_ALLOWED_TO_BE_DOWNLOADED_KEY,
      value: serializeDocumentViewSelections(selections, optionsByCategory),
      modified: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections]);

  const updateCategorySelection = (category, nextSelection) => {
    const previousSelection = selections[category] ?? [];
    const addedKeys = nextSelection.filter(
      (key) => !previousSelection.includes(key),
    );

    setSelections((current) => ({
      ...current,
      [category]: nextSelection,
    }));

    addedKeys.forEach((key) => {
      const matchingCategories = VIEW_CATEGORIES.filter(
        (otherCategory) =>
          otherCategory !== category &&
          (selections[otherCategory] ?? []).includes(key),
      );

      if (matchingCategories.length === 0) {
        return;
      }

      const displayText = findOptionDisplayText(key);
      setDuplicateInfoMessage(
        `"${displayText}" is also selected in ${formatCategoryList(matchingCategories)}.`,
      );
      setDuplicateInfoOpen(true);
    });
  };

  return (
    <>
    <FlexContainer
      width="100%"
      flexdirection="row"
      flexwrap="wrap"
      gap="20px"
      alignitems="flex-start"
      justifycontent="space-between"
      padding="25px"
      background={hex2rgba(theme.palette.primary.main, 0.1)}
    >
      {VIEW_CATEGORIES.map((category) => (
        <DocumentDefinitionsColumn
          key={category}
          columnTitle={DOCUMENT_VIEW_CATEGORY_LABELS[category]}
          options={optionsByCategory[category] ?? []}
          selection={selections[category] ?? []}
          onSelectionChange={(nextSelection) =>
            updateCategorySelection(category, nextSelection)
          }
          loading={loading}
        />
      ))}
    </FlexContainer>
    <ActionSnackbar
      severity="info"
      borderColor={theme.palette.info.main}
      vertical="top"
      horizontal="center"
      width="50%"
      isOpen={duplicateInfoOpen}
      closeFn={() => setDuplicateInfoOpen(false)}
      snackbarLabel={duplicateInfoMessage}
      hasAction={false}
      autoHideDuration={8000}
    />
    </>
  );
};

export default DocumentViewDefinitionsSetting;
